<template>
  <div
    id="scheduleContainer"
    ref="container"
  >
    <a
      v-if="showToggleButton"
      id="toggleBtn"
      href="#"
      @click="handleToggleButtonClick"
    >Toggle</a>
    <iframe
      id="openSchedulingFrame"
      ref="iframe"
      scrolling="no"
      :src="iframeUrl"
    />
  </div>
</template>

<script>
/* global $ */

export default {
  name: 'ReserveComponent',
  props: {
    iframeUrl: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      hostname: 'https://myuncchart.org/mychart',
      matchMediaString: '(max-width: 747px)',
      showToggleButton: false,
      toggleButtonPressed: false,
    };
  },
  created() {
    this.mediaQuery = window.matchMedia ? window.matchMedia(this.matchMediaString) : null;
  },
  methods: {
    init() {
      let elem = this.$refs.iframe;
      while (elem !== document) {
        elem.classList.add('keepshown');
        elem = elem.parentNode;
      }

      if (this.mediaQuery) {
        this.mediaQuery.addListener(this.onWindowResize.bind(this));
      }

      window.addEventListener('message', this.handleOSWidgetStateChange.bind(this));

      $(this.$refs.iframe)
        .load(() => {
          this.notifyIframe({ hostname: `${window.location.protocol}//${window.location.hostname}` });
        });
    },

    isMaximizeAllowed() {
      let hostPageIsNarrowEnough;

      if (window.matchMedia) {
        hostPageIsNarrowEnough = this.mediaQuery.matches;
      } else {
        // assume that matchMediaString takes the form '(max-width: 747px)'
        const maxWidth = this.matchMediaString.match(/\d+/)[0];
        hostPageIsNarrowEnough = (window.innerWidth <= maxWidth);
      }

      const currentOSStep = parseInt(this.$refs.iframe.getAttribute('data-oswStep'), 10);
      return (currentOSStep > 0) && hostPageIsNarrowEnough; // Allowed if not on 'Choose a Time' step
    },

    maximizeIfAllowed() {
      if (this.isMaximizeAllowed()) {
        this.maximize();
        return true;
      }
      return false;
    },

    maximize() {
      let classToAdd;

      if (parseInt(this.$refs.iframe.getAttribute('data-oswStep'), 10) >= 1) {
        // For sign up steps, we add viewportmax class to html, so everything without class 'keepshown' will be hidden.
        if (this.$refs.iframe.classList.contains('poppedOut')) {
          this.restoreWidgetDesktopState();
        }
        classToAdd = 'viewportmax';
      } else {
        // For time slots page, we still use the old position:fixed method for maximize so that incremental
        // loading still works.
        classToAdd = 'poppedOut';
        document.body.classList.add(classToAdd);
      }

      this.$refs.iframe.classList.add(classToAdd);
      this.$refs.container.classList.add(classToAdd);
      document.documentElement.classList.add(classToAdd);
    },

    restoreWidgetDesktopState(alsoSetParentScroll) {
      this.$refs.iframe.style.removeProperty('height');
      Array.prototype.forEach.call(
        document.querySelectorAll('.viewportmax'),
        (el) => el.classList.remove('viewportmax'),
      );
      Array.prototype.forEach.call(
        document.querySelectorAll('.poppedOut'),
        (el) => el.classList.remove('poppedOut'),
      );
      if (alsoSetParentScroll) {
        window.scrollTo(0, (window.pageYOffset + this.$refs.container.getBoundingClientRect().top) - 200);
      }
    },

    onWindowResize() {
      if (!this.maximizeIfAllowed()) {
        this.restoreWidgetDesktopState();
      }
    },

    handleToggleButtonClick(ev) {
      ev.preventDefault();
      this.toggleButtonPressed = true;
      if (document.body.classList.contains('poppedOut')) {
        this.maximize();
        this.notifyIframe({ maximized: true });
      } else {
        this.restoreWidgetDesktopState(true);
        this.notifyIframe({ minimized: true });
      }
      this.toggleButtonPressed = false;
    },

    notifyIframe(message) {
      try {
        this.$refs.iframe.contentWindow.postMessage(message, this.hostname);
      } catch (err) {
        // IE9 can't handle messages that are Objects, so iterate through each and send them individually.
        Object.keys(message)
          .forEach((key) => {
            this.$refs.iframe.contentWindow.postMessage(`${key}: ${message[key]}`, this.hostname);
          });
      }
    },

    handleOSWidgetStateChange(msgFromOtherWindow) {
      // Only handle messages from your Open Scheduling server
      if (msgFromOtherWindow.origin.indexOf(this.hostname) < 0) {
        return;
      }

      let parsedMsg;

      // IE9 support
      if (typeof msgFromOtherWindow.data === 'string') {
        const arrData = (msgFromOtherWindow.data).split(': ');
        parsedMsg = {};
        // eslint-disable-next-line prefer-destructuring
        parsedMsg[arrData[0]] = arrData[1];
      } else {
        parsedMsg = msgFromOtherWindow.data;
      }

      // OS widget changed to a different step in the workflow
      if (typeof parsedMsg.oswStep !== 'undefined') {
        const data = parsedMsg.oswStep;

        // Save the state as an attribute of the iframe
        this.$refs.iframe.setAttribute('data-oswstep', data);

        if (
          !this.maximizeIfAllowed()
          && (
            this.$refs.iframe.classList.contains('poppedOut')
            || this.$refs.iframe.classList.contains('viewportmax')
          )
        ) {
          this.restoreWidgetDesktopState(true);
        }
      }

      // OS widget is changing its layout
      switch (parsedMsg.layoutChange) {
        case 'toMobile':
          this.maximizeIfAllowed();
          break;
        case 'toDesktop':
          this.restoreWidgetDesktopState();
          break;
        case 'buttonOnly':
          this.$refs.iframe.className = 'buttonOnly';
          break;
        default:
      }

      if (parsedMsg.newHeight) {
        let newIframeHeight = parsedMsg.newHeight;
        if (window.innerHeight > newIframeHeight) {
          newIframeHeight = window.innerHeight;
        }
        if (this.$refs.iframe.className.indexOf('viewportmax') > 0) {
          this.$refs.iframe.style.height = `${newIframeHeight}px`;
        }
      }
      if (typeof parsedMsg.scrollTo !== 'undefined' && this.$refs.iframe.classList.contains('viewportmax') > 0) {
        $('html, body, .slider')
          .scrollTop(parsedMsg.scrollTo);
      }
    },
  },
};
</script>

<style lang="scss">
// defined in 3rd-party EmbeddedWidget.css
#openSchedulingFrame {
  border: none;
  overflow-y: scroll;
}

iframe {
  border: none;
  max-width: 100%;
  width: 100%;
}
</style>
